/**
 * Current Home NY - Gift Reggie registry checkout bridge
 *
 * Why this exists:
 * The theme replaces cart markup with AJAX after page load. Gift Reggie's app embed
 * can therefore lose a listener that was attached to the original checkout button.
 * This delegated handler is stable across cart-page, drawer, modal, and AJAX redraws.
 *
 * It activates ONLY when both are true:
 *   1) Gift Reggie's registry shipping cookie exists.
 *   2) The Shopify cart is tagged with attributes.registry_id.
 * Normal carts are untouched.
 */
(function () {
  'use strict';

  var CHECKOUT_SELECTOR = '[name="checkout"], #checkout, .ajax-cart__button-submit, .js-sticky-checkout-btn';
  var handlingCheckout = false;

  function getCookie(name) {
    var prefix = name + '=';
    var parts = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (part.indexOf(prefix) === 0) {
        var raw = part.substring(prefix.length);
        try { return decodeURIComponent(raw); } catch (e) {}
        try { return unescape(raw); } catch (e2) {}
        return raw;
      }
    }
    return null;
  }

  function parseRegistryAddress() {
    var raw = getCookie('giftreggie-address');
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) {
      // Some older Gift Reggie installs store an escaped JSON value.
      try { return JSON.parse(unescape(raw)); } catch (e2) { return null; }
    }
  }

  function shopRoot() {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) return Shopify.routes.root;
    return '/';
  }

  function cartUrl(path) {
    var root = shopRoot();
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + path.replace(/^\//, '');
  }

  var provinceMap = {
    AL:'Alabama', AK:'Alaska', AZ:'Arizona', AR:'Arkansas', CA:'California', CO:'Colorado',
    CT:'Connecticut', DE:'Delaware', DC:'District of Columbia', FL:'Florida', GA:'Georgia',
    HI:'Hawaii', ID:'Idaho', IL:'Illinois', IN:'Indiana', IA:'Iowa', KS:'Kansas', KY:'Kentucky',
    LA:'Louisiana', ME:'Maine', MD:'Maryland', MA:'Massachusetts', MI:'Michigan', MN:'Minnesota',
    MS:'Mississippi', MO:'Missouri', MT:'Montana', NE:'Nebraska', NV:'Nevada', NH:'New Hampshire',
    NJ:'New Jersey', NM:'New Mexico', NY:'New York', NC:'North Carolina', ND:'North Dakota',
    OH:'Ohio', OK:'Oklahoma', OR:'Oregon', PA:'Pennsylvania', RI:'Rhode Island', SC:'South Carolina',
    SD:'South Dakota', TN:'Tennessee', TX:'Texas', UT:'Utah', VT:'Vermont', VA:'Virginia',
    WA:'Washington', WV:'West Virginia', WI:'Wisconsin', WY:'Wyoming',
    AB:'Alberta', BC:'British Columbia', MB:'Manitoba', NB:'New Brunswick', NL:'Newfoundland and Labrador',
    NS:'Nova Scotia', NT:'Northwest Territories', NU:'Nunavut', ON:'Ontario', PE:'Prince Edward Island',
    QC:'Quebec', SK:'Saskatchewan', YT:'Yukon'
  };

  var countryMap = {
    US:'United States', USA:'United States', CA:'Canada', GB:'United Kingdom', UK:'United Kingdom',
    AU:'Australia', NZ:'New Zealand'
  };

  function checkoutQuery(address) {
    var fields = {
      first_name: address.first_name || '',
      last_name: address.last_name || '',
      company: address.company || '',
      address1: address.address1 || '',
      address2: address.address2 || '',
      city: address.city || '',
      zip: address.zip || '',
      country: countryMap[address.country] || address.country || '',
      province: provinceMap[address.province] || address.province || '',
      phone: address.phone || ''
    };

    var params = new URLSearchParams();
    Object.keys(fields).forEach(function (key) {
      if (fields[key] !== '') params.append('checkout[shipping_address][' + key + ']', fields[key]);
    });
    return params.toString();
  }

  function collectCartAttributes(button) {
    var attrs = {};
    var form = button && button.closest ? button.closest('form') : null;
    if (!form) return attrs;

    form.querySelectorAll('[name^="attributes["]').forEach(function (input) {
      var match = /^attributes\[([^\]]+)\]$/.exec(input.name || '');
      if (match) attrs[match[1]] = input.value;
    });
    return attrs;
  }

  function fallbackToNormalCheckout() {
    window.location.assign(cartUrl('checkout'));
  }

  function handleCheckout(button, event) {
    if (handlingCheckout) return;

    var registryAddress = parseRegistryAddress();
    if (!registryAddress) return; // Normal checkout / no registry address cookie.

    handlingCheckout = true;
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    if (button) button.disabled = true;

    fetch(cartUrl('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { if (!res.ok) throw new Error('Unable to read cart'); return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.attributes || !cart.attributes.registry_id) {
          // Stale Gift Reggie cookie or a non-registry cart: do not force registry shipping.
          fallbackToNormalCheckout();
          return null;
        }

        var attrs = collectCartAttributes(button);
        if (Object.keys(attrs).length === 0) return cart;

        return fetch(cartUrl('cart/update.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        }).then(function () { return cart; });
      })
      .then(function (cart) {
        if (!cart) return;
        var query = checkoutQuery(registryAddress);
        window.location.assign(cartUrl('checkout') + (query ? '?' + query : ''));
      })
      .catch(function (err) {
        console.error('[Gift Reggie cart bridge]', err);
        fallbackToNormalCheckout();
      })
      .finally(function () {
        handlingCheckout = false;
        if (button) button.disabled = false;
      });
  }

  // Capture phase makes this independent of when AJAX cart markup or app listeners are attached.
  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest ? event.target.closest(CHECKOUT_SELECTOR) : null;
    if (!button) return;
    if (!button.closest('form[action*="/cart"]')) return;
    handleCheckout(button, event);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart"]')) return;
    var button = form.querySelector(CHECKOUT_SELECTOR);
    if (!button) return;
    handleCheckout(button, event);
  }, true);
})();
